import {useIntl} from 'react-intl';
import {Ellipsis, Hand, PictureInPicture2, SlidersHorizontal} from 'lucide-react';
import {REACTION_ALLOWLIST, type Reaction} from '@phonehq/rtc-core';
import {groupCallMessages as gm} from '../../groupCalls/messages';
import {CallMenu, type CallMenuItem} from '../components/CallMenu';
import {BubbleControl, StageControl} from '../components/controls';
import {groupActions} from './groupActions';
import {groupSurfaceMessages as m} from './groupMessages';

interface GroupMoreMenuProps {
    readonly variant: 'bubble' | 'stage';
    readonly handRaised: boolean;
    readonly disabled?: boolean;
    readonly onPopOut?: () => void;
    readonly onOpenDevices?: () => void;
}

export const GroupMoreMenu = ({variant, handRaised, disabled, onPopOut, onOpenDevices}: GroupMoreMenuProps) => {
    const intl = useIntl();
    const iconSize = variant === 'bubble' ? 16 : 20;
    const items: CallMenuItem[] = [
        {
            id: 'hand',
            testId: 'group-call-hand',
            label: intl.formatMessage(handRaised ? gm.controlLowerHand : gm.controlRaiseHand),
            icon: <Hand size={iconSize} aria-hidden="true"/>,
            onSelect: () => groupActions.setHandRaised(!handRaised),
        },
        ...(onPopOut
            ? [{id: 'pip', testId: 'group-call-pop-out', label: intl.formatMessage(gm.controlPopOut), icon: <PictureInPicture2 size={iconSize} aria-hidden="true"/>, onSelect: onPopOut}]
            : []),
        ...(onOpenDevices
            ? [{id: 'devices', testId: 'group-call-settings', label: intl.formatMessage(m.deviceSettings), icon: <SlidersHorizontal size={iconSize} aria-hidden="true"/>, onSelect: onOpenDevices}]
            : []),
    ];
    const reactions = (
        <div role="group" aria-label={intl.formatMessage(gm.controlReactions)} className="mb-1 flex justify-between gap-1 border-b border-[var(--phq-call-line)] px-1 pb-2">
            {REACTION_ALLOWLIST.map(reaction => (
                <button
                    key={reaction}
                    type="button"
                    aria-label={reaction}
                    onClick={() => groupActions.react(reaction as Reaction)}
                    className="phq-call-press inline-flex h-9 w-9 items-center justify-center rounded-lg text-xl hover:bg-[var(--phq-call-surface-muted)]"
                >
                    {reaction}
                </button>
            ))}
        </div>
    );
    const Control = variant === 'bubble' ? BubbleControl : StageControl;
    return (
        <CallMenu
            items={items}
            extra={reactions}
            placement={variant === 'bubble' ? 'above-end' : 'above'}
            trigger={({open, toggle}) => (
                <Control
                    icon={<Ellipsis size={variant === 'bubble' ? 20 : 24} aria-hidden="true"/>}
                    label={intl.formatMessage(m.moreOptions)}
                    caption={intl.formatMessage(m.captionMore)}
                    tone={open ? 'pressed' : 'default'}
                    expanded={open}
                    hasPopup="menu"
                    disabled={disabled}
                    testId="group-call-more"
                    onClick={toggle}
                />
            )}
        />
    );
};
